import React from 'react'

import type {
  ChipData,
  HistogramSelection,
  PhenotypeSelectionValue,
  PhenotypeV3Selection,
  PhenotypicPreviewTag
} from '../preview-query/types'
import * as styles from './PreviewFilterTag.styles'
import { EqualsOperatorViewText } from './utils'

const mapArraySelectionToLabel = (values?: PhenotypeSelectionValue[]): string | undefined => {
  if (!Array.isArray(values) || values.length === 0) {
    return undefined
  }

  return values.map(({ label }) => label).join(', ')
}

const getHistogramSelectionLabel = (histogram?: HistogramSelection): string | undefined => {
  if (histogram === undefined || histogram === null || typeof histogram !== 'object') {
    return undefined
  }

  const hasLow = histogram.low !== undefined && histogram.low !== ''
  const hasHigh = histogram.high !== undefined && histogram.high !== ''

  if (!hasLow && !hasHigh) {
    return undefined
  }

  const lowLabel = hasLow ? String(histogram.low) : '?'
  const highLabel = hasHigh ? String(histogram.high) : '?'

  return `${lowLabel} - ${highLabel}`
}

const getSelectionLabel = (selection?: PhenotypeV3Selection): string | undefined => {
  if (!selection) {
    return undefined
  }

  return (
    mapArraySelectionToLabel(selection.bar) ??
    mapArraySelectionToLabel(selection.textSearch) ??
    getHistogramSelectionLabel(selection.histogram)
  )
}

interface PreviewFilterTagProps {
  tag: PhenotypicPreviewTag
}

export const PreviewFilterTag = ({ tag }: PreviewFilterTagProps): JSX.Element | null => {
  const fieldLabel = tag.fieldName?.trim() || (tag.fieldId !== undefined ? `Field ${tag.fieldId}` : undefined)
  const selectionLabel = tag.value?.trim() || getSelectionLabel(tag.selection)

  if (fieldLabel === undefined && selectionLabel === undefined) {
    return null
  }

  const chipContent: ChipData[] = []

  if (fieldLabel !== undefined) {
    chipContent.push({ type: 'text', value: fieldLabel })
  }

  if (selectionLabel !== undefined) {
    if (fieldLabel !== undefined) {
      chipContent.push({ type: 'operator', value: EqualsOperatorViewText })
    }

    chipContent.push({ type: 'value', value: selectionLabel })
  }

  if (chipContent.length === 0) {
    return null
  }

  return <styles.PreviewFilterChip withFixedWidth={false} data-testid="preview-filter-tag" content={chipContent} />
}
